import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { PaxSelectorContentRow, ErrorMessage } from '../../PaxSelector';
import { DpPaxMessages } from './DpPaxMessages';
import { KidSelector } from './KidSelector';
import {
    RoomsPaxProps,
    PaxConstants,
    DpPaxRestrictionPropsDefault,
    TabTypePropDefault,
    TabTypeProp, EntryPoint
} from '../../../models';
import { Div } from '../../../layouts';
import { ABUtils, TrackingUtils, withPageContext } from '../../../utils';

const FIELD_NAME_TRACKING = 'guest';

export const DpPaxContent = ({
    pax,
    paxRestrictions,
    onSelectKidAge,
    onChange,
    roomId,
    showKidError,
    isNative,
    isNonPrimeHotel,
    residentZones,
    residentProps,
    kidsAgeRange,
    trackEvent,
    activeProduct,
}) => {
    const { isCssVariation, isCssVariationMobile } = ABUtils.getABs();
    const Dprows = [
        {
            paxKey: PaxConstants.ADULTS,
            cmsKey: PaxConstants.ADULTS,
            min: paxRestrictions.minAdults,
            max: paxRestrictions.maxAdults,
            value: pax.adults,
        },
        {
            paxKey: PaxConstants.KIDS,
            cmsKey: 'children',
            min: paxRestrictions.minKids,
            max: paxRestrictions.maxKids,
            value: pax.kids,
        },
    ];

    const NonPrimeHotelRows = [
        {
            paxKey: PaxConstants.ROOMS,
            cmsKey: 'hotelRooms',
            min: paxRestrictions.minAdults,
            max: paxRestrictions.maxAdults,
            value: pax.rooms,
        },
        {
            paxKey: PaxConstants.ADULTS,
            cmsKey: PaxConstants.ADULTS,
            min: paxRestrictions.minAdults,
            max: paxRestrictions.maxAdults,
            value: pax.adults,
        },
        {
            paxKey: PaxConstants.KIDS,
            cmsKey: 'children',
            min: paxRestrictions.minKids,
            max: paxRestrictions.maxKids,
            value: pax.kids,
        },
    ];

    const isKids = (row) => row.paxKey === PaxConstants.KIDS;

    const rows = isNonPrimeHotel ? NonPrimeHotelRows : Dprows;

    const classNames = classnames({
        'odf-space-inner-top-m odf-space-inner-left-m odf-space-inner-right-m odf-box-content-md': !isCssVariation,
        'odf-space-inner-top-m': isCssVariation,
        'prisma-responsive-space-horizontal': isCssVariationMobile,
    });

    const getResidentMessage = () => {
        return residentZones && residentProps.regions.length && (pax.adults + pax.kids.length > 1)
            ? 'residentDiscount.main.ifSomePassengers'
            : '';
    };

    const residentMessage = getResidentMessage();

    useEffect(() => {
        if (activeProduct === EntryPoint.HOTELS) {
            const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
            const label = TrackingUtils.LABEL.fieldOpen(activeProduct, FIELD_NAME_TRACKING);
            trackEvent({ action, label });
        }
    }, []);

    return (
        <>
            <div className="odf-stroke-primary" />
            <Div className={classNames} data-testid="room-selector-item">
                {rows.map((row) => {
                    const isKidsResponse = isKids(row);
                    const rowId = isNonPrimeHotel ? row.paxKey : `${roomId}${row.paxKey}`;
                    const hideIfNoKid = pax.kids.length > 0;
                    return (
                        <React.Fragment key={rowId}>
                            <PaxSelectorContentRow
                                {...row}
                                value={
                                    isKidsResponse
                                        ? row.value.length
                                        : row.value
                                }
                                onChange={onChange}
                                ageCmsKey='roomSelector'
                            />
                            {isKidsResponse && hideIfNoKid && (
                                <KidSelector
                                    kids={row.value}
                                    onSelectKidAge={onSelectKidAge}
                                    showKidError={showKidError}
                                    isNative={isNative}
                                    kidsAgeRange={kidsAgeRange}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
                {!isNonPrimeHotel && (
                    <DpPaxMessages
                        kids={pax.kids.length}
                        restrictions={DpPaxRestrictionPropsDefault}
                    />
                )}
                {residentMessage && <ErrorMessage message={residentMessage} />}
            </Div>
            <div className="odf-stroke-primary" />
        </>
    );
};

DpPaxContent.propTypes = {
    onSelectKidAge: PropTypes.func.isRequired,
    pax: RoomsPaxProps.isRequired,
    paxRestrictions: PropTypes.shape({
        minAdults: PropTypes.number,
        maxAdults: PropTypes.number,
        minKids: PropTypes.number,
        maxKids: PropTypes.number,
    }).isRequired,
    roomId: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    showKidError: PropTypes.bool.isRequired,
    isNative: PropTypes.bool,
    numPax: PropTypes.number.isRequired,
    isNonPrimeHotel: PropTypes.bool.isRequired,
    residentZones: PropTypes.object.isRequired,
    residentProps: PropTypes.object.isRequired,
    kidsAgeRange: PropTypes.arrayOf(PropTypes.number),
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func.isRequired,
};

DpPaxContent.defaultProps = {
    isNative: false,
    activeProduct: TabTypePropDefault,
};

const mapStateToProps = ({ activeProduct }) => ({
    activeProduct: activeProduct.activeProduct,
});

const connected = connect(mapStateToProps, {})(withPageContext(DpPaxContent));

export { connected as ConnectedDpPaxContent };
